import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';

import { ProductList } from '../../components/product-list/product-list';
import { FlashDrivePrices } from '../../services/flash-drive-prices';
import {
  PriceFilter,
  PriceFilterPanel,
  PriceFilterSelectionEvent,
} from '../../components/price-filter-panel/price-filter-panel';
import {
  CapacityFilter,
  CapacityFilterPanel,
  CapacityFilterSelectionEvent,
} from '../../components/capacity-filter-panel/capacity-filter-panel';
import {
  BrandFilter,
  BrandFilterPanel,
  BrandFilterSelectionEvent,
} from '../../components/brand-filter-panel/brand-filter-panel';
import {
  HardwareInterfaceFilter,
  HardwareInterfaceFilterPanel,
  HardwareInterfaceFilterSelectionEvent,
} from '../../components/hardware-interface-filter-panel/hardware-interface-filter-panel';
import {
  DataSourceFilter,
  DataSourceFilterPanel,
  DataSourceFilterSelectionEvent,
} from '../../components/data-source-filter-panel/data-source-filter-panel';


import { forkJoin } from 'rxjs';

// Import filter state
import { priceFilterList, pricePanelTitle } from './filters/price-filters';
import { capacityFilterList, capacityPanelTitle } from './filters/capacity-filters';
import { brandPanelTitle } from './filters/brand-filters';
import { interfacePanelTitle } from './filters/interface-filters';
import {Link, LinkPanel} from '../../components/link-panel/link-panel';
import { dataSourceFilterList, dataSourcePanelTitle } from './filters/data-source-filters';

@Component({
  selector: 'app-home',
  imports: [
    ProductList,
    // DataSourceFilterPanel,
    PriceFilterPanel,
    CapacityFilterPanel,
    BrandFilterPanel,
    HardwareInterfaceFilterPanel,
    LinkPanel,
  ],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit {
  isBrowser: boolean;
  protected allProducts: any[] = [];
  rowData: any[] = [];

  // --- Filter State ---
  dataSourcePanelTitle = dataSourcePanelTitle;
  dataSourceFilterList = dataSourceFilterList;
  selectedDataSourceFilters: DataSourceFilter[] = [];

  pricePanelTitle = pricePanelTitle;
  priceFilterList = priceFilterList;
  selectedPriceFilters: PriceFilter[] = [];

  capacityPanelTitle = capacityPanelTitle;
  capacityFilterList = capacityFilterList;
  selectedCapacityFilters: CapacityFilter[] = [];

  brandPanelTitle = brandPanelTitle;
  brandFilterList: BrandFilter[] = [];
  selectedBrandFilters: BrandFilter[] = [];

  interfacePanelTitle = interfacePanelTitle;
  interfaceFilterList: HardwareInterfaceFilter[] = [];
  selectedInterfaceFilters: HardwareInterfaceFilter[] = [];

  // --- Links for the Link panel ---
  siteLinksTitle = 'Resources';
  siteLinks: Link[] = [
    { label: 'FAQ', url: '/faq' },
    { label: 'Privacy Policy', url: '/privacy-policy' },
  ];


  constructor(
    private flashDrivePrices: FlashDrivePrices,
    @Inject(PLATFORM_ID) private platformId: object,
    private title: Title,
    private metaService: Meta,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Initialize filter state arrays as empty.
    this.selectedPriceFilters = [];
    this.selectedCapacityFilters = [];
    this.selectedDataSourceFilters = [];
    this.selectedBrandFilters = [];
    this.selectedInterfaceFilters = [];

    // Use forkJoin to load all data streams in parallel
    forkJoin({
      products: this.flashDrivePrices.getFlaskDrivePrices(),
      brands: this.flashDrivePrices.getBrands(),
      interfaces: this.flashDrivePrices.getHardwareInterfaces(),
    }).subscribe(({ products, brands, interfaces }) => {
      // Process products
      this.allProducts = products;
      this.rowData = [...this.allProducts]; // Initially show all products

      // Process brands
      brands.sort((a, b) => a.localeCompare(b));
      this.brandFilterList = brands.map((brand, index) => ({
        id: index + 1,
        label: brand,
        selected: false, // Set to false
      }));

      // Process interfaces
      interfaces.sort((a, b) => a.localeCompare(b));
      this.interfaceFilterList = interfaces.map((iface, index) => ({
        id: index + 1,
        label: iface,
        selected: false, // Set to false
      }));

      // Apply filters on load (will show all products as none are selected)
      this.applyFilters();
    });
  }

  ngOnInit(): void {
    this.title.setTitle('Home | FlashDrivePrices');
    this.metaService.addTag({ name: 'description', content: 'Your one-stop destination for comparing prices and features on flash drives from all the top brands. We make it easy to find the perfect drive for your needs and budget.' });

  }
  onDataSourceFilterSelectionChange(event: DataSourceFilterSelectionEvent): void {
    this.selectedDataSourceFilters = event.selected;
    // The applyFilters() function would need to be updated to use this data.
    // this.applyFilters();
  }

  onPriceFilterSelectionChange(event: PriceFilterSelectionEvent): void {
    this.selectedPriceFilters = event.selected;
    this.applyFilters();
  }

  onCapacityFilterSelectionChange(event: CapacityFilterSelectionEvent): void {
    this.selectedCapacityFilters = event.selected;
    this.applyFilters();
  }

  onBrandFilterSelectionChange(event: BrandFilterSelectionEvent): void {
    this.selectedBrandFilters = event.selected;
    this.applyFilters();
  }

  onInterfaceFilterSelectionChange(event: HardwareInterfaceFilterSelectionEvent): void {
    this.selectedInterfaceFilters = event.selected;
    this.applyFilters();
  }

  applyFilters(): void {
    const hasActivePriceFilters = this.selectedPriceFilters?.length > 0;
    const hasActiveCapacityFilters = this.selectedCapacityFilters?.length > 0;
    const hasActiveBrandFilters = this.selectedBrandFilters?.length > 0;
    const hasActiveInterfaceFilters = this.selectedInterfaceFilters?.length > 0;

    // If no filters are selected in any category, show all products.
    if (!hasActivePriceFilters && !hasActiveCapacityFilters && !hasActiveBrandFilters && !hasActiveInterfaceFilters) {
      this.rowData = [...this.allProducts];
      return;
    }

    const selectedBrandNames = new Set(this.selectedBrandFilters.map((f) => f.label));
    const selectedInterfaceNames = new Set(this.selectedInterfaceFilters.map((f) => f.label));

    this.rowData = this.allProducts.filter((product) => {
      // A filter category is ignored if it has no selections (always returns true).
      // Otherwise, the product must match at least one selected item in that category.

      const priceMatch =
        !hasActivePriceFilters ||
        this.selectedPriceFilters.some((filter) => {
          const price = product.price;
          if (filter.minValue === undefined && filter.maxValue !== undefined)
            return price <= filter.maxValue;
          if (filter.minValue !== undefined && filter.maxValue === undefined)
            return price >= filter.minValue;
          if (filter.minValue !== undefined && filter.maxValue !== undefined)
            return price >= filter.minValue && price <= filter.maxValue;
          return false;
        });

      const capacityMatch =
        !hasActiveCapacityFilters ||
        this.selectedCapacityFilters.some((filter) => {
          const capacity = product.capacity; // in bytes
          if (filter.minBytes === undefined && filter.maxBytes !== undefined)
            return capacity > 0 && capacity <= filter.maxBytes;
          if (filter.minBytes !== undefined && filter.maxBytes === undefined)
            return capacity > filter.minBytes;
          if (filter.minBytes !== undefined && filter.maxBytes !== undefined)
            return capacity > filter.minBytes && capacity <= filter.maxBytes;
          return false;
        });

      const brandMatch = !hasActiveBrandFilters || selectedBrandNames.has(product.brand);

      const interfaceMatch = !hasActiveInterfaceFilters || selectedInterfaceNames.has(product.interface);

      // A product must satisfy the conditions for all active filter groups.
      return priceMatch && capacityMatch && brandMatch && interfaceMatch;
    });
  }
}
